import { Briefcase, Shield, Scale, Users, Building2, Award, Handshake, ArrowRight, Heart, Globe, Target } from 'lucide-react'
import SEOHead from '../components/SEOHead'
import { Link } from 'react-router-dom'

export default function Partners() {
  const partners = [
    {
      name: "Confederation of Passenger Transport",
      description: "The UK's premier trade association for the bus and coach industry, representing operators across the nation.",
      type: "Industry Association",
      logo: "/partnerlogos/cpt-uk.png",
      link: "/partners/cpt",
      cta: "View CPT Member Benefits"
    },
    {
      name: "Weightmans",
      description: "Top 40 law firm providing comprehensive legal support for transport operators through our C9 Legal Assist partnership.",
      type: "Legal Partner",
      logo: "/partnerlogos/Weightmans_Logo.svg.png",
      link: "/partners/weightmans",
      featured: true,
      cta: "Learn More About C9 Legal Assist"
    },
    {
      name: "ALBUM",
      description: "The Association of Local Bus Managers (ALBUM) supporting excellence in local bus management and operations.",
      type: "Management Association",
      logo: "/partnerlogos/ALBUM-Logo.png"
    },
    {
      name: "Chartered Institute of Logistics and Transport",
      description: "Professional body for supply chain, logistics and transport professionals, setting industry standards.",
      type: "Professional Institute",
      logo: "/partnerlogos/cilt-logo.png"
    },
    {
      name: "Women in Bus and Coach",
      description: "Supporting diversity and inclusion in the transport sector, promoting opportunities for women in the industry.",
      type: "Diversity Initiative",
      logo: "/partnerlogos/women in bus and coach logo.png"
    },
    {
      name: "Rebuilding Futures Alliance",
      description: "Helping individuals rebuild their lives through meaningful employment opportunities in transport and logistics.",
      type: "Social Impact",
      logo: "/partnerlogos/rfa logo.png"
    },
    {
      name: "Recruitment & Employment Confederation",
      description: "The professional body for the recruitment industry, ensuring best practices and ethical standards.",
      type: "Professional Body",
      logo: "/partnerlogos/rec-logo.png"
    }
  ]

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Our Partners - Strategic Partnerships | C9 Recruitment"
        description="Discover C9 Recruitment's strategic partnerships with leading organisations including Weightmans, CPT, CILT, ALBUM and more. Together, we're strengthening the transport and logistics sector."
        keywords="C9 Recruitment partners, Weightmans partnership, transport industry partnerships, logistics partnerships, recruitment partnerships, ALBUM"
        canonical="/partners"
      />
      
      {/* Compact Hero Section */}
      <section className="relative bg-gradient-to-br from-c9-blue via-c9-blue to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-c9-blue/80 to-transparent"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-c9-orange/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-white/5 rounded-full blur-lg"></div>
        
        <div className="relative container-full py-16 md:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-c9-orange/20 rounded-full text-c9-orange font-semibold text-sm mb-4 backdrop-blur-sm">
              Strategic Partnerships
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight">
              Stronger Together Through{" "}
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                Strategic Partnerships
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
              We collaborate with industry-leading organisations to provide comprehensive 
              solutions and support for the transport and logistics sector.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container-full">
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-c9-blue mb-6">
              Building a Stronger{" "}
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                Transport Industry
              </span>
            </h2>
            <p className="text-gray-600 leading-relaxed max-w-4xl mx-auto text-lg">
              At C9 Recruitment, we believe in the power of collaboration. Our strategic partnerships 
              with leading organisations across the transport, logistics, and professional services 
              sectors enable us to provide enhanced value and comprehensive solutions for our clients 
              and candidates.
            </p>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="pt-8 pb-16 md:pt-12 md:pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container-full">
          {/* Partners Header with Stats */}
          <div className="text-center mb-16">
            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mb-20">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-c9-orange">7+</div>
                <div className="text-sm text-gray-600">Partners</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-c9-orange">30,000+</div>
                <div className="text-sm text-gray-600">Network Reach</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-c9-orange">100%</div>
                <div className="text-sm text-gray-600">Coverage</div>
              </div>
            </div>
            
            {/* Section Header */}
            <div className="inline-block px-4 py-2 bg-c9-blue/10 rounded-full text-c9-blue font-semibold text-sm mb-6">
              Our Network
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-c9-blue mb-6">
              Industry-Leading{" "}
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                Partners
              </span>
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              We're proud to partner with organisations that share our commitment to 
              excellence in the transport and logistics sector.
            </p>
          </div>

          {/* Partners Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partners.map((partner, index) => (
              <div 
                key={index}
                className={`group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border ${
                  partner.featured ? 'border-c9-orange/30 ring-2 ring-c9-orange/10' : 'border-gray-100'
                } relative overflow-hidden`}
              >
                {partner.featured && (
                  <div className="absolute top-0 right-0 bg-gradient-to-l from-c9-orange to-orange-400 text-white text-xs font-semibold px-3 py-1 rounded-bl-lg">
                    Featured Partnership
                  </div>
                )}
                
                {/* Logo Section - Top Center */}
                <div className="text-center mb-6">
                  <div className="w-32 h-20 mx-auto bg-white rounded-xl shadow-md flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300 border border-gray-100">
                    <img 
                      src={partner.logo} 
                      alt={`${partner.name} logo`}
                      className="max-w-full max-h-full object-contain p-2"
                      onError={(e) => {
                        // Fallback in case image doesn't load
                        e.target.style.display = 'none'
                        e.target.nextSibling.style.display = 'flex'
                      }}
                    />
                    <div className={`hidden w-full h-full ${
                      partner.featured 
                        ? 'bg-gradient-to-br from-c9-orange to-orange-400' 
                        : 'bg-gradient-to-br from-c9-blue to-c9-blue-alt'
                    } rounded-xl items-center justify-center text-white text-sm font-bold`}>
                      {partner.name.split(' ').map(word => word[0]).join('').slice(0, 3)}
                    </div>
                  </div>
                  
                  {/* Partner Name */}
                  <h3 className={`text-xl font-bold mb-2 group-hover:text-c9-orange transition-colors ${
                    partner.featured ? 'text-c9-orange' : 'text-c9-blue'
                  }`}>
                    {partner.name}
                  </h3>
                  
                  {/* Type Badge */}
                  <span className={`inline-block px-3 py-1 ${
                    partner.featured 
                      ? 'bg-c9-orange/10 text-c9-orange' 
                      : 'bg-c9-blue/10 text-c9-blue'
                  } rounded-full text-xs font-medium mb-4`}>
                    {partner.type}
                  </span>
                </div>
                
                {/* Description */}
                <p className="text-gray-600 leading-relaxed text-center mb-6">
                  {partner.description}
                </p>
                
                {/* CTA Link */}
                {partner.link && (
                  <div className="text-center">
                    <Link 
                      to={partner.link}
                      className={`inline-flex items-center font-semibold transition-all duration-300 ${
                        partner.featured 
                          ? 'text-c9-orange hover:text-c9-orange-alt' 
                          : 'text-c9-blue hover:text-c9-orange'
                      } group`}
                    >
                      {partner.cta || 'Learn More'}
                      <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Benefits */}
      <section className="py-16 bg-white">
        <div className="container-full">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-c9-orange/10 rounded-full text-c9-orange font-semibold text-sm mb-4">
              Why Partner With Us
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-c9-blue mb-6">
              The Power of{" "}
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                Collaboration
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Globe className="h-8 w-8" />,
                title: "Extended Reach",
                description: "Access to wider networks and resources through our combined expertise and industry connections.",
                color: "from-blue-500 to-blue-600"
              },
              {
                icon: <Target className="h-8 w-8" />,
                title: "Shared Excellence",
                description: "Commitment to delivering the highest standards of service and support to the transport sector.",
                color: "from-c9-orange to-orange-400"
              },
              {
                icon: <Handshake className="h-8 w-8" />,
                title: "Mutual Growth",
                description: "Building long-term relationships that drive innovation and success for all stakeholders.",
                color: "from-green-500 to-green-600"
              }
            ].map((benefit, index) => (
              <div key={index} className="group text-center">
                <div className={`w-20 h-20 mx-auto bg-gradient-to-br ${benefit.color} rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-c9-blue mb-4">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-c9-orange to-orange-400 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl transform translate-x-32 -translate-y-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-2xl transform -translate-x-24 translate-y-24"></div>
        
        <div className="relative container-full text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Interested in Partnering With Us?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Join our network of industry-leading partners and help shape the future 
            of transport and logistics recruitment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-white text-c9-orange px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 inline-flex items-center justify-center"
            >
              Discuss Partnership Opportunities
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}